import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatMobile } from '../../lib/format.js';
import { useCustomerSearch } from '../../lib/customers/use-customers.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';

/** Who a quotation is being raised for — always a customer. */
export type QuotationParty = { kind: 'customer'; id: string; name: string };

/**
 * Who is this price for?
 *
 * Search-first, like every other "find the person" moment in the product — a shop with four
 * hundred customers cannot be handed a dropdown of them, and the counter knows the name or the
 * number it is looking for.
 *
 * CUSTOMERS ONLY. An enquiry is simply a customer with no orders yet, so somebody asking what a
 * lehenga would cost is found here like anybody else.
 *
 * There is no "quote somebody new" here on purpose: it would create a customer as a side effect
 * of pricing one. The empty state links to the customer book instead.
 */
export function QuotationPartyPickerDialog({
  open,
  onOpenChange,
  onPicked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPicked: (party: QuotationParty) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  // With nothing typed, the most recent customers — a counter that has just added somebody
  // finds them at the top without spelling their name.
  const customers = useCustomerSearch(
    { ...(trimmed ? { q: trimmed } : {}), pageSize: 8 },
    { enabled: open },
  );

  const items = customers.data?.items ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('quotations.picker.title')}</DialogTitle>
          <DialogDescription>{t('quotations.picker.hint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quotation-customer-search">{t('quotations.picker.searchLabel')}</Label>
          <Input
            id="quotation-customer-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('quotations.picker.searchPlaceholder')}
          />
        </div>

        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t('quotations.picker.empty')}{' '}
            <Link to="/customers" className="font-medium underline">
              {t('quotations.picker.toCustomers')}
            </Link>
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {items.length > 0 ? (
              <PartyGroup heading={t('quotations.picker.customersHeading')}>
                {items.map((customer) => (
                  <PartyRow
                    key={customer.id}
                    name={customer.name}
                    detail={formatMobile(customer.primaryPhone)}
                    onPick={() =>
                      onPicked({ kind: 'customer', id: customer.id, name: customer.name })
                    }
                  />
                ))}
              </PartyGroup>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A labelled group of one book's matches. Rendered only when that book has any. */
function PartyGroup({ heading, children }: { heading: string; children: ReactNode }): ReactElement {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {heading}
      </h4>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  );
}

function PartyRow({
  name,
  detail,
  onPick,
}: {
  name: string;
  detail: string;
  onPick: () => void;
}): ReactElement {
  return (
    <li>
      <button
        type="button"
        className="flex w-full flex-col rounded-lg border border-border p-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onPick}
      >
        <span className="font-medium">{name}</span>
        <span className="text-sm text-muted-foreground">{detail}</span>
      </button>
    </li>
  );
}

export default QuotationPartyPickerDialog;
